import type { NavItem } from '@base-shared/types/navigation';
import type { components as GroupComponents } from '@base-shared/types/group.openapi';
import type { components as SubscriptionsComponents } from '@base-shared/types/subscriptions.openapi';
import type { components as PlayerComponents } from '@base-shared/types/player.openapi';

export enum AUTH_STEP {
  SIGN_UP_VERIFY_ACCOUNT = 'SIGN_UP_VERIFY_ACCOUNT',
  SIGN_UP_DONE = 'SIGN_UP_DONE',
  SIGN_UP_AUTO_SIGNED_IN = 'SIGN_UP_AUTO_SIGNED_IN',
  LOG_IN_ENTER_EMAIL = 'LOG_IN_ENTER_EMAIL',
  LOG_IN_ENTER_PASSWORD = 'LOG_IN_ENTER_PASSWORD',
  LOG_IN_SET_NEW_PASSWORD = 'LOG_IN_SET_NEW_PASSWORD',
  LOG_IN_VERIFY_CODE = 'LOG_IN_VERIFY_CODE',
  LOG_IN_UPDATE_DETAILS = 'LOG_IN_UPDATE_DETAILS',
  LOG_IN_ACCEPT_TOS = 'LOG_IN_ACCEPT_TOS',
  LOG_IN_VERIFY_ACCOUNT = 'LOG_IN_VERIFY_ACCOUNT',
  LOG_IN_DONE = 'LOG_IN_DONE',
  SIGN_OUT_DONE = 'SIGN_OUT_DONE',
  RESET_PASSWORD_CONFIRM_WITH_CODE = 'RESET_PASSWORD_CONFIRM_WITH_CODE',
  RESET_PASSWORD_DONE = 'RESET_PASSWORD_DONE',
  SOCIAL_SIGN_IN = 'SOCIAL_SIGN_IN',
  SOCIAL_SIGN_IN_FAILED = 'SOCIAL_SIGN_IN_FAILED',
  SOCIAL_SIGN_IN_DONE = 'SOCIAL_SIGN_IN_DONE',
  SSO_ATTEMPT_TO_MIGRATE = 'SSO_ATTEMPT_TO_MIGRATE',
  SSO_PROCEED_TO_MIGRATE_AND_LOGIN = 'SSO_PROCEED_TO_MIGRATE_AND_LOGIN',
  SSO_PROCEED_TO_MIGRATE = 'SSO_PROCEED_TO_MIGRATE',
  SSO_MIGRATE_CONFIRMED = 'SSO_MIGRATE_CONFIRMED'
}

export const PRODUCT_ARYA = 'arya';
export const PRODUCT_ASSETS = 'assets';
export const PRODUCT_ACTIVATE = 'activate';
export const PRODUCT_INTEGRATIONS = 'integrations';

export const PRODUCTS: Readonly<Record<string, GroupComponents['schemas']['Product']>> = {
  ARYA: PRODUCT_ARYA,
  ASSETS: PRODUCT_ASSETS,
  ACTIVATE: PRODUCT_ACTIVATE,
  INTEGRATIONS: PRODUCT_INTEGRATIONS
} as const;

export const enum APP {
  ARYA = 'arya',
  ASSETS = 'assets',
  HUB = 'hub',
  HUB_SETTINGS = 'hub-settings',
  ACTIVATE = 'activate',
  INTEGRATIONS = 'integrations'
};

// List of apps that have sign up enabled
export const APPS_WITH_SIGN_UP_ENABLED = [APP.ASSETS, APP.HUB, APP.INTEGRATIONS];

export const HUB_SETTINGS = {
  value: 'hub-settings',
  name: 'Settings',
  icon: 'spx:hub-logo',
  iconWithName: 'spx:settings-logo-name',
  badgeClass: 'bg-white text-spinetix-900'
};

export const HUB_HOME = {
  value: 'hub',
  name: 'Hub',
  productName: 'SpinetiX HUB',
  icon: 'spx:hub-logo',
  iconWithName: 'spx:hub-home-logo-name',
  badgeClass: 'bg-spinetix-900 text-white'
};

export const ARYA = {
  value: PRODUCT_ARYA,
  name: 'ARYA',
  productName: 'SpinetiX ARYA',
  icon: 'spx:arya-logo',
  iconWithName: 'spx:arya-logo-name',
  badgeClass: 'bg-arya-500 text-white'
};

export const ASSETS = {
  value: PRODUCT_ASSETS,
  name: 'Assets',
  productName: 'Assets',
  icon: 'spx:assets-logo',
  iconWithName: 'spx:assets-logo-name',
  badgeClass: 'bg-assets-500 text-spinetix-900'
};

export const ACTIVATE = {
  value: PRODUCT_ACTIVATE,
  name: 'Activate',
  productName: 'Activate',
  icon: 'spx:activate-logo',
  iconWithName: 'spx:activate-logo-name',
  badgeClass: 'bg-primary-800 text-white'
};

export const INTEGRATIONS = {
  value: PRODUCT_INTEGRATIONS,
  name: 'Integrations',
  productName: 'Integrations',
  icon: 'spx:integrations-logo',
  iconWithName: 'spx:integrations-logo-name',
  badgeClass: 'bg-integrations-500 text-spinetix-900'
};

export const ENTERPRISE_DEMO_NAME = 'Demo';
export const ENTERPRISE_TRIAL_NAME = 'Trial';
export const ENTERPRISE_WIDGETS_NAME = 'WIDGETS';
export const ENTERPRISE_KIOSK_NAME = 'KIOSK';
export const ENTERPRISE_SYSTEMS_NAME = 'SYSTEMS';

export const PRODUCT_LIST = [ARYA, ASSETS, ACTIVATE, INTEGRATIONS];

export const SIDE_MENU_PRODUCTS = [
  ...PRODUCT_LIST,
  HUB_SETTINGS,
  HUB_HOME
];

export const VALID_PRODUCTS: GroupComponents['schemas']['Product'][] = Object.values(PRODUCTS);

export const DEFAULT_NOTIF_DURATION = 10000;
export const DEFAULT_SHORT_NOTIF_DURATION = 5000;

export const TIMEOUT_100_MS = 100;
export const TIMEOUT_300_MS = 300;
export const TIMEOUT_500_MS = 500;
export const TIMEOUT_1000_MS = 1000;
export const TIMEOUT_3000_MS = 3000;
export const TIMEOUT_5000_MS = 5000;

export const DEFAULT_POLLING_TIMEOUT_MS = 180000; // 3 minutes total timeout

export const DEFAULT_DATATABLE_MAX_QUERY_COUNT = 10;

export const HUB_STAGE_INFO = 'HUB_STAGE_INFO';

export const HUB_SESSION = 'HUB_SESSION';

export enum ARYA_PLANS {
  DISCOVERY = 'discovery',
  PREMIUM = 'premium',
  ENTERPRISE = 'enterprise',
  ENTERPRISE_DEMO = 'enterprise-demo',
  ENTERPRISE_TRIAL = 'enterprise-trial',
  ENTERPRISE_WIDGETS = 'enterprise-widgets',
  ENTERPRISE_KIOSK = 'enterprise-kiosk',
  ENTERPRISE_SYSTEMS = 'enterprise-systems'
};

/**
 * Represents all possible user rights, including those that can be modified by the user
 * as defined in {@link GroupComponents.schemas.UserModifiableRights}, as well as the special
 * 'development' right used for development purposes.
 */
type AllUserRights = GroupComponents['schemas']['UserModifiableRights'] | 'development';

/**
 * An object containing all possible user rights within the application.
 *
 * Includes both global and ARYA-specific rights, as well as special rights such as 'development'.
 * The keys are descriptive names for each right, and the values are the corresponding string identifiers
 * used throughout the system.
 *
 * @example
 * USER_RIGHTS.GLOBAL_ADMIN // 'account_admin'
 * USER_RIGHTS.ARYA_ADMIN // 'user_admin'
 * USER_RIGHTS.DEVELOPMENT // 'development'
 *
 * @see {@link GroupComponents.schemas.UserModifiableRights}
 */
export const USER_RIGHTS = {
  GLOBAL_ADMIN: 'account_admin',
  DEVELOPMENT: 'development',
  SUBSCRIPTION_MANAGER: 'subscription_manager',

  // ARYA specific rights
  ARYA_ADMIN: 'user_admin',
  ARYA_BILLING: 'user_billing',
  ARYA_ALERT: 'user_alert',
  ARYA_PLAYER: 'user_player',
  ARYA_SCHEDULE: 'user_schedule',
  ARYA_CREATE: 'user_create',
  ARYA_CREATE_LOCK: 'user_create_lock',
  ARYA_LIMITED: 'user_limited'
} as const satisfies Record<string, AllUserRights>;

/**
 * CONNECTION_APPS contains the configuration for various
 * connector apps. Each app has an icon, a short description
 * translation key, and a readOnly flag. The readOnly flag
 * indicates whether the app is in a read-only state meaning
 * it should not be used to create a new connection.
 */
export const CONNECTION_APPS = {
  'azure': {
    icon: 'social:microsoft-office-365',
    shortDescription: 'connectorApps.azure.shortDescription',
    readOnly: false
  },
  'powerbi': {
    icon: 'social:microsoft-powerbi',
    shortDescription: 'connectorApps.powerbi.shortDescription',
    readOnly: false
  },
  'google': {
    icon: 'social:google',
    shortDescription: 'connectorApps.google.shortDescription',
    readOnly: false
  },
  'flickr': {
    icon: 'social:flickr',
    shortDescription: 'connectorApps.flickr.shortDescription',
    readOnly: false
  },
  'yammer': {
    icon: 'social:microsoft-viva-engage',
    shortDescription: 'connectorApps.yammer.shortDescription',
    readOnly: false
  },
  'yammer-legacy': {
    icon: 'social:microsoft-yammer-legacy',
    shortDescription: 'connectorApps.yammer-legacy.shortDescription',
    readOnly: true
  }
};

/**
 * PASSWORD_REGEX explanation:
 * - At least 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one digit
 * - At least one special character (including backslash)
 *
 * Note: To include backslash, it must be escaped twice in a JS regex: '\\\\'
 */
export const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~])[A-Za-z\d!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]{8,}$/;
export const PASSWORD_SPECIAL_CHARS_REGEX = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/;

export const SOCIAL_PROVIDERS = {
  GOOGLE: 'Google',
  FACEBOOK: 'Facebook'
} as const;

/**
 * HELP_MENU_NAV_ITEMS defines the list of navigation items
 * to be displayed in the help menu.
 *
 * This should be used wherever the help menu navigation is
 * rendered across different applications.
 *
 * See {@link NavItem} for more details on the structure.
 */
export const HELP_MENU_NAV_ITEMS: Readonly<NavItem[]> = [
  {
    id: 'help-center',
    value: 'help-center',
    name: 'labels.helpCenter',
    externalLink: 'helpCenter'
  },
  {
    id: 'release-notes',
    value: 'release-notes',
    name: 'labels.releaseNotes',
    externalLink: 'releaseNotes'
  },
  {
    id: 'keyboard-shortcuts',
    value: 'keyboard-shortcuts',
    name: 'labels.keyboardShortcuts',
    externalLink: 'keyboardShortcuts'
  },
  {
    id: 'privacy',
    value: 'privacy',
    name: 'labels.privacy'
  }
] as const;

/**
 * Contains the configuration for the help menu navigation
 * items and the legal page with route as /legal.
 *
 * See {@link NavItem} for more details on the structure.
 */
export const HUB_HELP_MENU_NAV_ITEMS: Readonly<NavItem[]> = [
  ...HELP_MENU_NAV_ITEMS,
  {
    id: 'legal',
    value: 'legal',
    name: 'labels.legal',
    routeName: ['legal'],
    fullPath: '/legal'
  }
] as const;

/**
 * Contains the configuration for the help menu navigation
 * items specific to the HUB Settings app. It includes the
 * legal page with route as /settings/legal.
 *
 * See {@link NavItem} for more details on the structure.
 */
export const HUB_SETTINGS_HELP_MENU_NAV_ITEMS: Readonly<NavItem[]> = [
  ...HELP_MENU_NAV_ITEMS,
  {
    id: 'legal',
    value: 'legal',
    name: 'labels.legal',
    routeName: ['settings-legal'],
    fullPath: '/settings/legal'
  }
] as const;

/**
 * SUBSCRIPTION_SCOPES defines the mapping between product identifiers
 * and their corresponding subscription scopes used in the system.
 */
export type SubscriptionProductScope = SubscriptionsComponents['schemas']['SubscriptionProduct']['scope'];
export const SUBSCRIPTION_SCOPES = {
  ARYA: 'arya-plan',
  FEATURE_SET: 'dsos-feature-set',
  DEMO_FEATURE_SET: 'dsos-demo-feature-set',
  ELEMENTI: 'elementi'
} as const satisfies Record<string, SubscriptionProductScope>;

/**
 * SUBSCRIPTION_PRODUCT_TECHNICAL_IDS defines the technical
 * IDs for subscription product catalog as defined in the
 * backend API. These technical IDs are used to identify
 * specific subscription products when interacting with the
 * API.
 */
export type SubscriptionProductTechnicalId = SubscriptionsComponents['schemas']['SubscriptionProduct']['technicalId'];
export const SUBSCRIPTION_PRODUCT_TECHNICAL_IDS = {
  ARYA_DISCOVERY: 'arya-discovery',
  ARYA_PREMIUM: 'arya-premium',
  ARYA_ENTERPRISE_DEMO: 'arya-enterprise-demo',
  ARYA_ENTERPRISE: 'arya-enterprise',
  FEATURE_SET_KIOSK: 'feature-set-kiosk',
  FEATURE_SET_SYSTEMS: 'feature-set-systems',
  FEATURE_SET_WIDGETS: 'feature-set-widgets'
} as const satisfies Record<string, SubscriptionProductTechnicalId>;
export type SubscriptionRequestProductTechnicalId = typeof SUBSCRIPTION_PRODUCT_TECHNICAL_IDS[keyof typeof SUBSCRIPTION_PRODUCT_TECHNICAL_IDS];

// Feature Sets
export const FEATURE_SETS_LOGO = {
  name: 'Feature Sets',
  iconName: 'logo:feature-set'
};

/**
 * Group's Feature set types as defined in the backend API.
 */
export const FEATURE_SET_WIDGETS = 'widgets';
export const FEATURE_SET_KIOSK = 'kiosk';
export const FEATURE_SET_SYSTEMS = 'systems';

export const FEATURE_SET_TYPES = {
  [FEATURE_SET_WIDGETS]: {
    name: ENTERPRISE_WIDGETS_NAME,
    icon: 'logo:license-widgets',
    iconColor: 'bg-license-widgets',
    textColor: 'text-license-widgets',
    technicalId: SUBSCRIPTION_PRODUCT_TECHNICAL_IDS.FEATURE_SET_WIDGETS
  },
  [FEATURE_SET_KIOSK]: {
    name: ENTERPRISE_KIOSK_NAME,
    icon: 'logo:license-kiosk',
    iconColor: 'bg-license-kiosk',
    textColor: 'text-license-kiosk',
    technicalId: SUBSCRIPTION_PRODUCT_TECHNICAL_IDS.FEATURE_SET_KIOSK
  },
  [FEATURE_SET_SYSTEMS]: {
    name: ENTERPRISE_SYSTEMS_NAME,
    icon: 'logo:license-systems',
    iconColor: 'bg-license-systems',
    textColor: 'text-license-systems',
    technicalId: SUBSCRIPTION_PRODUCT_TECHNICAL_IDS.FEATURE_SET_SYSTEMS
  }
};

export type FeatureSetState = PlayerComponents['schemas']['FeatureSetState'];
export const FEATURE_SET_ASSIGNED = 'assigned';
export const FEATURE_SET_ASSIGN_PENDING = 'assign-pending';
export const FEATURE_SET_REVOKE_PENDING = 'revoke-pending';

export const FEATURE_SET_MODAL_TYPES = {
  ASSIGN: 'assign',
  REMOVE: 'remove',
  ASSIGN_PROCESSING: 'assign-processing',
  ASSIGN_DONE: 'assign-done',
  REMOVE_PROCESSING: 'remove-processing',
  REMOVE_DONE: 'remove-done',
  ASSIGN_CANCEL: 'assign-cancel',
  REMOVE_CANCEL: 'remove-cancel'
} as const;
/**
 * Modal type for feature set operations
 * - assign: Show assignment interface
 * - remove: Show removal confirmation
 * - remove-processing: Show removal in progress
 * - remove-done: Show removal completed
 */
export type FeatureSetModalType = typeof FEATURE_SET_MODAL_TYPES[keyof typeof FEATURE_SET_MODAL_TYPES];

// Storage units in order from smallest to largest
export const STORAGE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;

export const KB = STORAGE_UNITS[1]; // 'KB'
export const GB = STORAGE_UNITS[3]; // 'GB'
export const DEFAULT_STORAGE_IN_KB = 1000000; // 1 GB in KB
export const DEFAULT_STORAGE_IN_GB = 1; // 1 GB
export const CLOUD_STORAGE_GB_PER_PLAYER = 10; // 10 GB of cloud storage per player

/**
 * Define the minimum and maximum range of months for subscription expiration dates.
 * These constants are used to set the allowable range for subscription expiration
 * when creating or renewing subscriptions.
 */
export const SUBSCRIPTION_DEFAULT_EXPIRATION_RANGE_MIN_MONTHS = 12;
export const SUBSCRIPTION_DEFAULT_EXPIRATION_RANGE_MAX_MONTHS = 60;
/** Define the default maximum player capacity for subscriptions. */
export const SUBSCRIPTION_DEFAULT_MAX_PLAYER_CAPACITY = 3000;

/**
 * SUBSCRIPTION_REQUEST_VARIANT defines the different variants of subscription requests
 * used in the frontend application. Each variant corresponds to a specific type of
 * subscription action, such as upgrading, renewing, or adding capacity.
 *
 * @see {@link SubscriptionsComponents} for the components['schemas']['RequestType'].
 */
export const SUBSCRIPTION_REQUEST_VARIANT = {
  UPGRADE_ENTERPRISE: 'upgrade-enterprise',
  UPGRADE_FEATURE_SETS: 'upgrade-feature-sets',
  RENEWAL_ALL: 'renew-all',
  RENEWAL_ENTERPRISE: 'renew-enterprise',
  RENEWAL_FEATURE_SETS: 'renew-feature-sets',
  ADD_CAPACITY_ENTERPRISE: 'add-capacity-enterprise',
  ADD_CAPACITY_FEATURE_SETS: 'add-capacity-feature-sets'
} as const;

export type SubscriptionRequestVariant = typeof SUBSCRIPTION_REQUEST_VARIANT[keyof typeof SUBSCRIPTION_REQUEST_VARIANT];

export const SUBSCRIPTION_REQUEST_TYPE_MAP = {
  'renew': {
    class: 'bg-green-100 text-green-800',
    icon: 'flowbite:refresh-outline',
    label: 'labels.renew'
  },
  'upgrade': {
    class: 'bg-purple-100 text-purple-800',
    icon: 'flowbite:rocket-solid',
    label: 'labels.upgrade'
  },
  'add-capacity': {
    class: 'bg-pink-100 text-pink-800',
    icon: 'flowbite:circle-plus-solid',
    label: 'labels.addCapacity'
  }
};

export type SubscriptionAlertMilestone = '3mos' | '2mos' | '1mo' | 'expired' | 'suspended';
export const SUBSCRIPTION_ALERTS_MILESTONES = [
  {
    milestone: '3mos',
    valueInMonths: 3
  },
  {
    milestone: '2mos',
    valueInMonths: 2
  },
  {
    milestone: '1mo',
    valueInMonths: 1
  },
  {
    milestone: 'expired',
    valueInMonths: 0
  },
  {
    milestone: 'suspended',
    valueInMonths: null
  }
] as const satisfies Readonly<Array<{
  milestone: SubscriptionAlertMilestone;
  valueInMonths: number | null;
}>>;

export const AMPLIFY_AUTH_STEPS = {
  DONE: 'DONE',
  CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
  CONFIRM_RESET_PASSWORD_WITH_CODE: 'CONFIRM_RESET_PASSWORD_WITH_CODE',
  CONFIRM_SIGN_UP: 'CONFIRM_SIGN_UP'
} as const;
